import React, { useState, useEffect, useCallback } from 'react';
import { round3 } from '../utils/money';
import { Link, useNavigate } from 'react-router-dom';
import Swal from '../theme/swal';
import {
  IcPackageOpen, IcShoppingCart, IcShoppingBag, IcUsers, IcLineChart, IcAlertTriangle,
  IcFileText, IcCalendarCheck, IcSparkles, IcClock, IcBanknote,
  IcClipboardList, IcCheckCircle, IcXCircle,
  IcWallet, IcTrophy, IcHourglass, IcCoins,
  IcClose, IcArrowLeft, IcTruck, IcReceipt, IcLayoutGrid,
  IcFilters,
} from '../theme/icons';
import SalesTrendChart from '../components/SalesTrendChart';
import DashboardNotesChat from '../components/DashboardNotesChat';
import ReservationProductActions from '../components/ReservationProductActions';
import PurchaseSalesBarChart from '../components/PurchaseSalesBarChart';
import dashboardService from '../services/dashboardService';
import { peekCached } from '../services/apiCache';
import { useLiveRefresh } from '../realtime';
import supplierService from '../services/supplierService';
import chargeService from '../services/chargeService';
import paymentService from '../services/paymentService';
import factureService from '../services/factureService';
import serviceManagementService from '../services/serviceManagementService';
import authService from '../services/authService';
import KI from '../theme/palette';
import { BtnSk, CardSkeleton, Sk } from '../components/Skeleton';

const QUICK_ACTIONS = [
  // Sélection Studio (Ahmed 2026-08-17) : les 4 avatars identiques — fond
  // gris clair (gray-100), icône + nom encre (gray-900), bordure 1.5px encre.
  { to: '/seller-sales', label: 'Nouvelle Vente', icon: IcShoppingCart },
  { action: 'commande', label: 'Réserver produit', icon: IcClipboardList },
  { action: 'achat', label: 'Achat et paiement', icon: IcShoppingBag },
  { action: 'soin', label: 'Réserver un soin', icon: IcHourglass },
];

// Filtres de la carte « Réservation de produits ». Le groupe d'action reprend la
// logique des boutons de ReservationProductActions : 0 = « Marquer arrivée » bleu
// (pending, stock entré), 1 = « Bloquer » (arrivée), 2 = « Marquer arrivée » grisé
// (pending, stock pas encore entré via facture), 3 = bloquée.
const resaCanArrive = (r) => {
  const stockKnown = r.product_stock !== undefined && r.product_stock !== null;
  return !stockKnown || (Number(r.product_stock) || 0) >= (r.quantity || 1);
};
const resaGroup = (r) => {
  if (r.status === 'pending') return resaCanArrive(r) ? 0 : 2;
  if (r.status === 'arrived') return 1;
  return 3;
};
const RESA_FILTERS = [
  { key: 'priorite', label: 'Priorité', desc: 'Marquer arrivée → Bloquer → en attente de stock', dot: 'bg-blue-500' },
  { key: 'arrivee', label: 'Marquer arrivée', desc: 'Produit en stock — bouton bleu', dot: 'bg-blue-500', group: 0 },
  { key: 'bloquer', label: 'À bloquer', desc: 'Client arrivé — produit à bloquer', dot: 'bg-emerald-500', group: 1 },
  { key: 'attente_stock', label: 'En attente de stock', desc: 'Marquer arrivée grisé — facture pas encore saisie', dot: 'bg-gray-400', group: 2 },
  { key: 'bloquee', label: 'Bloquée', desc: 'Produit déjà bloqué pour le client', dot: 'bg-teal-500', group: 3 },
  { key: 'toutes', label: 'Toutes', desc: 'Toutes les réservations actives', dot: 'bg-gray-300' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  // Financial sections (Ventes / jour, Achats / jour) are manager/admin only.
  // Sellers get the operational cards (sessions, réservations, stock, clients).
  const canManage = authService.isManagerOrAdmin();
  // Dashboard KPIs/series cover a fixed 30-day window (period selector removed).
  const [period] = useState('30d');
  // Last known answer painted at once (no skeleton); the fetch below refreshes it.
  const [data, setData] = useState(() => peekCached('dashboard/', { period: '30d' }) || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // « Actions rapides » pop — open by default, toggle to open/close
  const [actionsOpen, setActionsOpen] = useState(true);
  // Hauteur réelle de la barre de navigation (sticky, h-16 mais elle grandit
  // avec le menu mobile) : le voile sombre du pop commence juste en dessous,
  // pour que le header reste exactement tel quel (owner 2026-09-08).
  const [navH, setNavH] = useState(64);
  useEffect(() => {
    const nav = document.querySelector('nav.kp-header');
    if (!nav) return undefined;
    const measure = () => setNavH(Math.round(nav.getBoundingClientRect().bottom));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(nav);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  // Top clients card view: 'points' (loyalty) | 'credit' (reste à payer)
  const [clientsView, setClientsView] = useState('points');
  // Carte « Réservation de produits » : filtre choisi en cliquant le titre.
  // Défaut « priorite » : Marquer arrivée (bleu) → Bloquer → arrivée grisée.
  const [resaFilter, setResaFilter] = useState('priorite');
  const [resaFilterOpen, setResaFilterOpen] = useState(false);
  // « Achat et paiement » popup
  const [achatOpen, setAchatOpen] = useState(false);
  // step: 'menu' (Achat | Paiement) → Achat: 'achat' (facture | commande)
  //                                 → Paiement: 'type' (fournisseur | charge) → 'form'
  const [payStep, setPayStep] = useState('menu');
  const [payType, setPayType] = useState(null); // 'supplier' | 'charge'
  const [payTargets, setPayTargets] = useState([]); // suppliers or charges
  const [payTargetsLoading, setPayTargetsLoading] = useState(false);
  const [payTargetId, setPayTargetId] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  const openAchat = () => {
    setPayStep('menu');
    setPayType(null);
    setPayTargets([]);
    setPayTargetId('');
    setPayAmount('');
    setPayNote('');
    setAchatOpen(true);
  };

  const goFacture = () => {
    setAchatOpen(false);
    navigate('/facturation/add');
  };

  const goCommande = () => {
    setAchatOpen(false);
    navigate('/commandes/a-commander');
  };

  const choosePayType = async (type) => {
    setPayType(type);
    setPayTargetId('');
    setPayStep('form');
    setPayTargetsLoading(true);
    try {
      const list = type === 'supplier'
        ? await supplierService.getAllSuppliers()
        : await chargeService.getAll();
      setPayTargets(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading payment targets:', err);
      setPayTargets([]);
      Swal.fire('Erreur', 'Impossible de charger la liste.', 'error');
    } finally {
      setPayTargetsLoading(false);
    }
  };

  const submitPayment = async () => {
    const amount = round3(parseFloat(payAmount));
    if (!payTargetId) {
      Swal.fire('Champ requis', `Veuillez sélectionner ${payType === 'supplier' ? 'un fournisseur' : 'une charge'}.`, 'warning');
      return;
    }
    if (!amount || amount <= 0) {
      Swal.fire('Montant invalide', 'Veuillez saisir un montant payé supérieur à 0.', 'warning');
      return;
    }
    setPaySubmitting(true);
    try {
      // For a SUPPLIER payment, apply the amount to the supplier's unpaid factures
      // (oldest first) so the supplier's balance actually goes down — same logic as
      // an "acompte" on the supplier detail page. Without this the payment was only
      // logged and the supplier's reste à payer never moved.
      if (payType === 'supplier') {
        let factures = [];
        try {
          factures = await factureService.getAllFactures({ supplier_id: payTargetId, unpaid: true });
        } catch (e) {
          factures = [];
        }
        const unpaid = (factures || [])
          .filter((f) => (Number(f.amount_due) || 0) > 0)
          .sort((a, b) => new Date(a.date_added || 0) - new Date(b.date_added || 0));
        const totalDue = unpaid.reduce((s, f) => s + (Number(f.amount_due) || 0), 0);
        const EPS = 1e-6;
        // Pay the whole balance exactly when the amount covers it (avoid float dust).
        const payAll = amount >= totalDue - 5e-4;
        let remaining = payAll ? totalDue : amount;
        for (const f of unpaid) {
          if (remaining <= EPS) break;
          const due = Number(f.amount_due) || 0;
          if (due <= 0) continue;
          const pay = remaining >= due - EPS ? due : remaining;
          if (pay <= 0) continue;
          await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
          remaining -= pay;
        }
      }
      const payload = {
        payment_type: payType,
        amount,
        note: payNote || '',
        ...(payType === 'supplier' ? { supplier: payTargetId } : { charge: payTargetId }),
      };
      await paymentService.create(payload);
      setAchatOpen(false);
      const targetName = payTargets.find((t) => String(t.id) === String(payTargetId))?.name || '';
      Swal.fire({
        icon: 'success',
        title: 'Paiement enregistré',
        text: `${amount.toFixed(3)} DT — ${payType === 'supplier' ? 'Fournisseur' : 'Charge'}${targetName ? ` : ${targetName}` : ''}.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error creating payment:', err);
      Swal.fire('Erreur', "Impossible d'enregistrer le paiement.", 'error');
    } finally {
      setPaySubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const d = await dashboardService.getDashboardData(period);
        if (active) { setData(d); setError(null); }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (active) setError('Impossible de charger le tableau de bord.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [period]);

  const refreshDashboard = async () => {
    try { const d = await dashboardService.getDashboardData(period); setData(d); } catch (e) { console.error('Refresh dashboard failed:', e); }
  };

  // Something changed on the server (a sale, a reservation…): refresh quietly.
  useLiveRefresh(useCallback(() => {
    dashboardService.getDashboardData(period).then(setData).catch(() => {});
  }, [period]));

  // Sessions du jour — Annuler (cancel) / Terminer (go collect payment in the panier).
  const handleCancelSession = async (e, s) => {
    e.stopPropagation();
    const res = await Swal.fire({
      title: 'Annuler la session ?', text: `${s.service_name} — ${s.customer_name}`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Oui, annuler', cancelButtonText: 'Non',
    });
    if (!res.isConfirmed) return;
    try {
      await serviceManagementService.updateSessionStatus(s.id, 'cancelled');
      await refreshDashboard();
    } catch (err) {
      console.error('Cancel session failed:', err);
      Swal.fire('Erreur', "Impossible d'annuler la session.", 'error');
    }
  };
  const handleFinishSession = async (e, s) => {
    e.stopPropagation();
    // A soin already paid at booking has nothing left to collect — complete it
    // now. An UNPAID one is only completed once the sale is actually encaissée
    // in the panier (sessionId travels with the deep-link): abandoning the
    // panier must leave the session open, not marked « Terminé ».
    if (s.payment_status === 'paid') {
      try { await serviceManagementService.updateSessionStatus(s.id, 'completed'); } catch (err) { console.error('Finish session status failed:', err); }
    }
    // Go to the vente page: add this soin to the cart AT ITS BOOKED price/remise,
    // select the client, open the panier — products can be added to the same sale.
    // A soin already paid at booking is not re-added (it would charge twice).
    navigate('/seller-sales', {
      state: {
        addServiceId: s.service_id,
        serviceName: s.service_name,
        servicePrice: s.service_price,
        serviceRemise: s.service_remise,
        soinPaid: s.payment_status === 'paid',
        clientId: s.customer_id,
        sessionId: s.id,
        openPanier: true,
      },
    });
  };

  // Rank badge color (1st gold, 2nd silver, 3rd bronze).
  const rankColor = (i) => i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-500';

  const formatCurrency = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;
  const formatDate = (s) => new Date(s).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });

  // Order sessions: scheduled (up) → in progress → terminé → annulé (down).
  const statusOrder = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled': case 'planifié': return 0;
      case 'in_progress': case 'confirmed': case 'confirmé': return 1;
      case 'completed': case 'terminé': return 2;
      case 'cancelled': case 'annulé': return 3;
      default: return 1;
    }
  };

  // Étiquette par statut de séance (Etiquettes-2 §7) : Programmé teinte
  // verte, En cours vert pâle, Terminé vert plein, Annulé gris neutre.
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled': case 'planifié': return 'bdg-pos';
      case 'in_progress': case 'confirmed': case 'confirmé': return 'bdg-live';
      case 'completed': case 'terminé': return 'bdg-done';
      case 'cancelled': case 'annulé': return 'bdg-neutral';
      default: return 'bdg-warn';
    }
  };
  const statusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled': case 'planifié': return 'Programmé';
      case 'in_progress': case 'confirmed': case 'confirmé': return 'En cours';
      case 'completed': case 'terminé': return 'Terminé';
      case 'cancelled': case 'annulé': return 'Annulé';
      default: return status;
    }
  };

  // Types de service (DIAGNOSTIC / SOIN / ACTE) → étiquettes bordées bdg-type-* (§8).
  const serviceTypeBadge = (type) => {
    switch (String(type || '').toUpperCase()) {
      case 'DIAGNOSTIC': return 'bdg-type-diag';
      case 'SOIN': return 'bdg-type-soin';
      case 'ACTE': return 'bdg-type-acte';
      default: return 'bdg-neutral';
    }
  };
  const serviceTypeLabel = (type) => ({ DIAGNOSTIC: 'Diagnostique', SOIN: 'Soin', ACTE: 'Acte' }[String(type || '').toUpperCase()] || type);

  const kpis = data?.kpis || {};
  const prev = data?.prev || {};
  const renderDelta = (curr, prevVal) => {
    if (!curr && !prevVal) return null;
    const pct = prevVal > 0 ? ((curr - prevVal) / prevVal) * 100 : (curr > 0 ? 100 : 0);
    const up = curr > prevVal;
    const flat = Math.abs(pct) < 0.5;
    // Tendance (livre §04) : texte seul 700 — ▲ +N% / ▼ −N% / = 0% — le
    // triangle porte le sens, jamais la couleur seule (pas d'icône Lucide).
    if (flat) return <span className="bdg bdg-flat">= 0%</span>;
    return (
      <span className={`bdg ${up ? 'bdg-up' : 'bdg-down'}`}>
        {up ? '▲ +' : '▼ −'}{Math.abs(pct).toFixed(0)}%
      </span>
    );
  };

  // Réservations de la carte selon le filtre choisi (défaut : tri par priorité).
  const resaAll = data?.reserved_products || [];
  const activeResaFilter = RESA_FILTERS.find((f) => f.key === resaFilter) || RESA_FILTERS[0];
  const shownResa = activeResaFilter.group !== undefined
    ? resaAll.filter((r) => resaGroup(r) === activeResaFilter.group)
    : (resaFilter === 'priorite' ? [...resaAll].sort((a, b) => resaGroup(a) - resaGroup(b)) : resaAll);
  const resaCount = (f) => (f.group !== undefined ? resaAll.filter((r) => resaGroup(r) === f.group).length : resaAll.length);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>
      )}

      {/* « Actions rapides » — FIXED en haut à droite (sous le header h-16),
          toujours visible au scroll, comme le bouton chat en bas à droite.
          Brand Kit §03 emplacement 1 : noir encre #111827, survol identique. */}
      <button
        onClick={() => setActionsOpen((v) => !v)}
        className="btn-add-top fixed top-20 right-6 z-30 px-5 py-3 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
      >
        <IcLayoutGrid /> Actions rapides
      </button>

      {/* Row A — Sessions du jour (haut gauche) + Réservation de produits (haut droite) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Today's sessions */}
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2"><IcClock className="text-teal-600" /> Sessions du jour</h2>
            <Link to="/reservations" className="text-sm font-semibold text-iface-600 hover:text-gray-900">Voir tout →</Link>
          </div>
          {/* Hauteur fixe : les deux cartes de la rangée gardent la même taille
              quel que soit le nombre de lignes (1 client = même carte). */}
          <div className="h-96 overflow-y-auto space-y-3">
            {(!data?.today_sessions || data.today_sessions.length === 0) ? (
              <div className="empty-ki py-8"><IcCalendarCheck className="empty-ki-icon w-9 h-9 mb-2" /><p className="empty-ki-title text-sm mt-0">Aucune session aujourd'hui</p></div>
            ) : [...data.today_sessions].sort((a, b) => statusOrder(a.status) - statusOrder(b.status)).map((s) => (
              <div key={s.id} className="bg-white border-[1.5px] border-green-100 p-3 rounded-xl">
                <div className="flex justify-between items-start mb-1.5">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{s.customer_name}</p>
                    {s.customer_phone && <p className="text-xs text-gray-500 truncate">{s.customer_phone}</p>}
                  </div>
                  {/* « Programmé » retiré du tableau de bord (owner 2026-09-06) : l'état par
                      défaut n'est plus étiqueté, seuls En cours / Terminé / Annulé le sont. */}
                  {!['scheduled', 'planifié'].includes(String(s.status || '').toLowerCase()) && (
                    <span className={`bdg shrink-0 ${getStatusColor(s.status)}`}>{statusLabel(s.status)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-sm text-gray-700 font-medium truncate">{s.service_name}</span>
                  {/* « Soin » retiré (owner 2026-09-06) : type par défaut non étiqueté. */}
                  {s.service_type && String(s.service_type).toUpperCase() !== 'SOIN' && <span className={`bdg ${serviceTypeBadge(s.service_type)}`}>{serviceTypeLabel(s.service_type)}</span>}
                  {/* Heure de la séance retirée (owner 2026-09-06). Son `ml-auto` passe sur le
                      prix, qui reste donc calé à droite. Prix aligné sur « Avance … » des
                      cartes de réservation : 12 px, 700, encre #111827, chiffres tabulaires. */}
                  <span className="ml-auto text-xs font-bold text-gray-900 tabular-nums">{formatCurrency(s.service_price)}</span>
                </div>
                {statusOrder(s.status) < 2 && (
                  <div className="flex gap-2">
                    <button onClick={(e) => handleCancelSession(e, s)} className="btn-g flex-1 px-2 py-1.5 text-xs flex items-center justify-center gap-1 transition-colors"><IcXCircle size={11} /> Annuler</button>
                    {/* « Terminer » passé au kit PRIMAIRE vert (.btn-p) — owner 2026-09-06. */}
                    <button onClick={(e) => handleFinishSession(e, s)} className="btn-p flex-1 px-2 py-1.5 text-xs flex items-center justify-center gap-1 transition-colors"><IcCheckCircle size={11} /> Terminer</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Réservation de produits — réservations actives */}
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex justify-between items-center mb-4">
            {/* Cliquer le titre ouvre le choix du filtre de la carte. */}
            <div className="relative">
              <button
                onClick={() => setResaFilterOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={resaFilterOpen}
                title="Choisir un filtre"
                className="font-semibold text-gray-800 flex items-center gap-2 rounded-lg px-1.5 py-1 -mx-1.5 bg-white border border-sky-100 hover:bg-blue-50 transition-colors"
              >
                <IcClipboardList className="text-sky-700" /> Réservation de produits
                {/* chip de filtre actif (livre §05) : pill grise #F3F4F6 / #374151 */}
                <span className="chip-x gap-1 px-2 py-0.5 text-[11px]" title={activeResaFilter.label} aria-label={activeResaFilter.label}>
                  <IcFilters size={9} />
                </span>
              </button>
              {resaFilterOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setResaFilterOpen(false)} />
                  <div role="menu" className="absolute left-0 top-full mt-1 z-30 w-80 max-w-[calc(100vw-3rem)] bg-white border border-gray-200 rounded-xl shadow-lg p-1.5">
                    {RESA_FILTERS.map((f) => (
                      <button
                        key={f.key}
                        role="menuitemradio"
                        aria-checked={resaFilter === f.key}
                        onClick={() => { setResaFilter(f.key); setResaFilterOpen(false); }}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 transition-colors ${resaFilter === f.key ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${f.dot}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-gray-800">{f.label}{f.key === 'priorite' ? ' (défaut)' : ''}</span>
                          <span className="block text-xs text-gray-500 truncate">{f.desc}</span>
                        </span>
                        <span className="text-xs font-semibold text-gray-400 shrink-0">{resaCount(f)}</span>
                        {resaFilter === f.key && <IcCheckCircle className="text-blue-600 shrink-0" size={14} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Link to="/reservations" className="text-sm font-semibold text-iface-600 hover:text-gray-900">Voir tout →</Link>
          </div>
          <div className="h-96 overflow-y-auto space-y-2">
            {shownResa.length === 0 ? (
              <div className="empty-ki py-8">
                <IcClipboardList className="empty-ki-icon w-9 h-9 mb-2" />
                <p className="empty-ki-title text-sm mt-0">
                  {resaAll.length > 0 ? 'Aucune réservation pour ce filtre' : 'Aucune réservation produit'}
                </p>
              </div>
            ) : shownResa.map((r) => (
              <div key={r.id} className="bg-white border border-sky-100 p-3 rounded-xl">
                <div className="flex justify-between items-start mb-1 gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{r.product_name} <span className="text-gray-400 font-normal">× {r.quantity}</span></p>
                    <p className="text-xs text-gray-500 truncate">{r.customer_name}{r.customer_phone ? ` · ${r.customer_phone}` : ''}</p>
                  </div>
                  {r.expected_arrival_date && <span className="text-xs text-gray-400 flex items-center gap-1 shrink-0"><IcClock size={10} /> {formatDate(r.expected_arrival_date)}</span>}
                </div>
                <div className="flex items-center justify-between">
                  {/* Statut de résa (Etiquettes-2 §6) : seule « Bloquée » (encre) reste affichée.
                      « En attente » et « Arrivée » retirées du tableau de bord (owner 2026-09-06) —
                      le statut se lit sur les boutons d'action. `ml-auto` garde l'avance à droite
                      quand l'étiquette n'est plus rendue. */}
                  {r.status === 'blocked' && <span className="bdg bdg-ink">Bloquée</span>}
                  {Number(r.deposit_amount) > 0 && <b className="text-xs tabular-nums text-gray-900 ml-auto">Avance {formatCurrency(r.deposit_amount)}</b>}
                </div>
                <ReservationProductActions reservation={r} onChanged={refreshDashboard} className="mt-2 pt-2 border-t border-white justify-end" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row B — Stock faible (bas gauche) + Top clients (bas droite) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Low stock */}
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2"><IcAlertTriangle className="text-red-600" /> Stock faible</h2>
            <Link to="/commandes/a-commander" className="text-sm font-semibold text-red-600 hover:text-red-800">Commander →</Link>
          </div>
          <div className="min-h-[244px] max-h-96 overflow-y-auto space-y-2">
            {(!data?.low_stock_products || data.low_stock_products.length === 0) ? (
              <div className="empty-ki py-8"><IcCheckCircle className="empty-ki-icon w-9 h-9 mb-2" /><p className="empty-ki-title text-sm mt-0">Tous les stocks sont bons</p></div>
            ) : data.low_stock_products.map((p) => (
              <div key={p.id} className="bg-white border-[1.5px] border-red-100 p-3 rounded-2xl flex justify-between items-center">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{p.name}</p>
                  <p className="text-sm text-gray-500">Stock : <span className="font-bold text-red-600">{p.stock}</span> · Min/sem : {p.stock_min}{p.sold_qty != null && <span className="text-gray-400"> · {p.sold_qty} vendus (30j)</span>}</p>
                </div>
                <IcAlertTriangle className="text-red-500 shrink-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Top clients — toggle: loyalty points | reste à payer */}
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex justify-between items-center mb-4 gap-2">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2 min-w-0">
              {clientsView === 'points'
                ? <><IcTrophy className="text-amber-500 shrink-0" /> <span className="truncate">Top 10 points clients</span></>
                : <><IcWallet className="text-orange-500 shrink-0" /> <span className="truncate">Top clients à payer</span></>}
            </h2>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setClientsView((v) => (v === 'points' ? 'credit' : 'points'))} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 font-medium transition-colors">
                {clientsView === 'points' ? 'Reste à payer' : 'Points'}
              </button>
              <Link to="/clients" className="text-sm text-amber-600 hover:text-amber-700">Clients →</Link>
            </div>
          </div>
          <div className="min-h-[244px] max-h-96 overflow-y-auto space-y-2">
            {clientsView === 'points' ? (
              (!data?.top_clients || data.top_clients.length === 0) ? (
                <div className="empty-ki flex flex-col items-center justify-center h-[244px]"><IcUsers className="empty-ki-icon w-12 h-12 mb-3" /><p className="empty-ki-title text-sm mt-0">Aucun client</p></div>
              ) : data.top_clients.map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 bg-white border-[1.5px] border-amber-100 p-2.5 rounded-2xl">
                  <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${rankColor(i)}`}>{i + 1}</span>
                  <p className="font-semibold text-gray-800 truncate flex-1 min-w-0">{c.name}</p>
                  <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-amber-600"><IcCoins size={12} /> {c.points.toFixed(0)}</span>
                </div>
              ))
            ) : (
              (!data?.top_clients_credit || data.top_clients_credit.length === 0) ? (
                <div className="empty-ki flex flex-col items-center justify-center h-[244px]"><IcWallet className="empty-ki-icon w-12 h-12 mb-3" /><p className="empty-ki-title text-sm mt-0">Aucun impayé</p></div>
              ) : data.top_clients_credit.map((c, i) => (
                <div key={c.id} className="flex items-center gap-3 bg-white border-[1.5px] border-amber-100 p-2.5 rounded-2xl">
                  <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${rankColor(i)}`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-800 truncate">{c.name}</p>
                    {c.phone && <p className="text-xs text-gray-400 truncate">{c.phone}</p>}
                  </div>
                  <span className="shrink-0 text-sm font-bold text-orange-600">{formatCurrency(c.montant)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Row C — Ventes / jour (big card) — manager/admin only */}
      {canManage && (
      <div className="mb-6">
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2"><IcLineChart className="text-green-700" /> Ventes / jour</h2>
            <Link to="/seller-history" className="text-xs font-semibold text-iface-600 hover:text-gray-900">Historique →</Link>
          </div>

          {/* 4 cases : Ventes · Chiffre d'affaires (+ Encaissé) · Capital · Bénéfice
              — cartes KPI blanches d'information (Composants-2 §5).
              2026-09-07 (owner, Studio) : les 3 cases d'argent prennent un filet
              1.5px teinté + leur valeur en 900 — CA vert (#DCFCE7 / #14532D),
              Capital rouge (#FEE2E2 / #B91C1C), Bénéfice or (#FEF3C7 / #B45309) —
              les deux valeurs reprises en 700 le même jour : ce sont les jetons
              « texte sur fond clair » de la palette (coralText / goldText, AA).
              « Ventes » (un compte, pas un montant) garde le filet gris de .kpi. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {/* 1 — Ventes */}
            <div className="kpi min-w-0 rounded-2xl p-3">
              <IcShoppingCart size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1">Ventes</div>
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="kpi-value text-base sm:text-lg truncate">{kpis.count || 0}</span>
                {renderDelta(kpis.count, prev.count)}
              </div>
            </div>
            {/* 2 — Chiffre d'affaires (+ Encaissé plus petit dessous) */}
            <div className="kpi min-w-0 rounded-2xl p-3 border-[1.5px] border-green-100">
              <IcBanknote size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1">Chiffre d'affaires</div>
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="kpi-value text-base sm:text-lg truncate text-green-900">{formatCurrency(kpis.ca)}</span>
                {renderDelta(kpis.ca, prev.ca)}
              </div>
              <div className="kpi-sub mt-1 pt-1 text-[11px]">
                Encaissé <span className="font-semibold text-gray-700 tabular-nums">{formatCurrency(kpis.encaisse)}</span>
              </div>
            </div>
            {/* 3 — Capital (coût des ventes TTC) */}
            <div className="kpi min-w-0 rounded-2xl p-3 border-[1.5px] border-red-100">
              <IcCoins size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1">Capital</div>
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="kpi-value text-base sm:text-lg truncate text-red-700">{formatCurrency(data?.capital)}</span>
              </div>
              <div className="kpi-sub text-[10px] mt-0.5">Coût des ventes</div>
            </div>
            {/* 4 — Bénéfice (+ marge) */}
            <div className="kpi min-w-0 rounded-2xl p-3 border-[1.5px] border-amber-100">
              <IcLineChart size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1">Bénéfice</div>
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="kpi-value text-base sm:text-lg truncate text-amber-700">{formatCurrency(kpis.benefice)}</span>
                {renderDelta(kpis.benefice, prev.benefice)}
              </div>
              <div className="kpi-sub text-[10px] mt-0.5">Marge {(kpis.marge || 0).toFixed(0)}%</div>
            </div>
          </div>

          {/* Courbe cliquable → statistiques de ventes. CA (vert) · Capital (rouge) · Bénéfice (bleu) */}
            {/* Survol des 2 cartes graphes (owner 2026-09-07) : cadre neutre CLAIR (ring gray-200 — le noir gray-900 était « trop fort ») et
                indication « Cliquez… » en noir GRAS — plus de rouge ni de bleu au survol. */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/statistiques/ventes')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/statistiques/ventes'); }}
            className="cursor-pointer group rounded-xl transition hover:ring-2 hover:ring-gray-200"
            title="Voir les statistiques de ventes détaillées"
          >
            {/* Légende CA · Coût · Bénéfice SOUS la courbe (owner 2026-09-07). */}
            <SalesTrendChart series={data?.series} light legend="bottom" />
            <p className="text-center text-[11px] text-gray-400 mt-1 group-hover:text-gray-900 group-hover:font-semibold">Cliquez sur la courbe pour les statistiques de ventes détaillées →</p>
          </div>
        </div>
      </div>
      )}

      {/* Row D — Achats / jour (KPI cards + weekly achats vs ventes bar chart) — manager/admin only */}
      {canManage && (
      <div className="mb-6">
        <div className="kp-card bg-white rounded-2xl border border-gray-200 p-5">
          {/* En-tête « Achats / jour » (titre + mention « Impayés & top produits »)
              retiré le 2026-09-07 (owner, Studio). Les 5 autres cartes gardent
              le leur : le sélecteur du Studio les visait toutes les 6, mais elles
              portent aussi des commandes (Filtres, bascule Top clients,
              Historique →) qui seraient parties avec. */}
          {/* Weekly comparison — achats (factures) vs ventes (8 dernières semaines) */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><IcLineChart className="text-sky-500" /> Achats vs Ventes / semaine</h3>
              <span className="text-xs text-gray-400">8 dernières semaines</span>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate('/statistiques/generale')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/statistiques/generale'); }}
              className="cursor-pointer group rounded-xl transition hover:ring-2 hover:ring-gray-200"
              title="Voir les statistiques générales"
            >
              {/* Légende Ventes · Achats SOUS le diagramme et centrée (owner 2026-09-07). */}
              <PurchaseSalesBarChart data={data?.weekly_compare} achatColor={KI.sapphire} light legend="bottom" />
              <p className="text-center text-[11px] text-gray-400 mt-1 group-hover:text-gray-900 group-hover:font-semibold">Cliquez sur le diagramme pour les statistiques générales →</p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* « Actions rapides » pop — floats over the dashboard like the Panier.
          Deux couches, toujours montées pour pouvoir fondre (260 ms) à
          l'ouverture ET à la fermeture (`invisible` coupe clics/focus une fois
          le fondu fini, sans rien laisser derrière) :
            1. le VOILE : fixed, de `navH` (bas réel de la nav) au bas de
               l'écran, MÊME voile que « Achat et paiement » (`.modal-backdrop` :
               encre 45 %, sans flou — owner 2026-09-08) ; il assombrit le
               contenu du tableau de bord mais s'arrête sous le header ;
            2. la COUCHE des 4 boutons : fixed inset-0 transparente, centrée
               comme avant (les boutons ne bougent pas), au-dessus du voile ;
               un clic sur le vide ferme le pop.
          Ordre z : contenu (0) < voile (z-40) < boutons (z-40, après dans le
          DOM) < nav (sticky z-50, jamais couverte ni assombrie). */}
      <div
        aria-hidden="true"
        style={{ top: navH }}
        className={`modal-backdrop left-0 right-0 bottom-0 z-40 transition-[opacity,visibility] duration-[260ms] ease-out motion-reduce:transition-none ${actionsOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      />
      <div
        className={`fixed inset-0 z-40 flex items-center justify-center p-4 overflow-y-auto transition-[opacity,visibility] duration-[260ms] ease-out motion-reduce:transition-none ${actionsOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        onMouseDown={(e) => { if (e.target === e.currentTarget) setActionsOpen(false); }}
      >
          <div className="my-8">
            {/* Juste les 4 avatars flottants — la grille est à la taille exacte des
                avatars pour que l'espace vertical et horizontal soit identique. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 place-items-center">
              {QUICK_ACTIONS.map((a) => {
                const inner = (
                  <>
                    {/* Grand avatar ; le nom est écrit dedans, sous l'icône. */}
                    <span className="flex flex-col items-center justify-center gap-4 w-56 h-56 rounded-[2.75rem] px-4 bg-gray-100 border-[1.5px] border-gray-900 shadow-xl transition-all duration-200 group-hover:scale-105 group-hover:shadow-2xl">
                      <a.icon className="text-7xl" />
                      <p className="text-xl font-bold leading-tight tracking-tight text-center">{a.label}</p>
                    </span>
                  </>
                );
                const base = 'group flex items-center justify-center text-gray-900 transition-all duration-200';
                const hover = 'hover:-translate-y-1';
                if (a.to) {
                  return <Link key={a.label} to={a.to} className={`${base} ${hover}`}>{inner}</Link>;
                }
                if (a.action === 'achat') {
                  return (
                    <button key={a.label} onClick={() => { setActionsOpen(false); openAchat(); }} className={`${base} ${hover} text-left`}>
                      {inner}
                    </button>
                  );
                }
                if (a.action === 'commande') {
                  return (
                    <button key={a.label} onClick={() => { setActionsOpen(false); navigate('/seller-sales', { state: { commande: true } }); }} className={`${base} ${hover} text-left`}>
                      {inner}
                    </button>
                  );
                }
                if (a.action === 'soin') {
                  // Opens the Réservations page (calendrier des soins) — the rendez-vous
                  // is booked there, directly dans le panneau du jour.
                  return (
                    <button key={a.label} onClick={() => { setActionsOpen(false); navigate('/reservations'); }} className={`${base} ${hover} text-left`}>
                      {inner}
                    </button>
                  );
                }
                return <div key={a.label} className={`${base} opacity-90 cursor-default`} title="Bientôt disponible">{inner}</div>;
              })}
            </div>
          </div>
      </div>

      {/* Notes — discussion entre les comptes (bouton flottant en bas à droite) */}
      <DashboardNotesChat />

      {/* « Achat et paiement » popup */}
      {achatOpen && (
        <div className="modal-backdrop z-50 flex items-center justify-center p-4" onClick={() => !paySubmitting && setAchatOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header — entête de modale (Brand Kit §05) : encre 13/16px, ✕ 17px gray-400 */}
            <div className="modal-head p-5 shrink-0 rounded-t-2xl">
              <div className="flex items-center gap-2">
                {payStep !== 'menu' && (
                  <button
                    onClick={() => setPayStep(payStep === 'form' ? 'type' : 'menu')}
                    className="text-gray-400 hover:text-white p-1 -ml-1"
                    title="Retour"
                  >
                    <IcArrowLeft />
                  </button>
                )}
                <h2 className="modal-title flex items-center gap-2">
                  {payStep === 'menu' ? 'Achat et paiement'
                    : payStep === 'achat' ? "Type d'achat"
                    : payStep === 'type' ? 'Type de paiement'
                    : `Paiement — ${payType === 'supplier' ? 'Fournisseur' : 'Charge'}`}
                </h2>
              </div>
              <button onClick={() => !paySubmitting && setAchatOpen(false)} className="text-gray-400 hover:text-white p-1 shrink-0" aria-label="Fermer" title="Fermer">
                <IcClose />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto">
              {/* Les 6 grandes cartes de ce sélecteur (Achat/Paiement, Nouvelle
                  facture/commande, Fournisseur/Charge) sont TOUTES en encre
                  #111827 depuis le 2026-09-07 (owner, Studio) : filet 1.5px,
                  icône et libellé encre. Chacune avait avant son propre liseré
                  (bleu, émeraude, jaune, gris) — le survol coloré du filet a été
                  retiré avec, sinon la couleur revenait au passage de la souris ;
                  le fond gray-100 → gray-200 suffit comme retour visuel. La
                  sous-légende reste gray-500 (hiérarchie). */}
              {payStep === 'menu' && (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setPayStep('achat')}
                    className="flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcFileText className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900">Achat</span>
                    <span className="text-xs text-gray-500 text-center">Facture ou commande</span>
                  </button>
                  <button
                    onClick={() => setPayStep('type')}
                    className="flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcBanknote className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900">Paiement</span>
                    <span className="text-xs text-gray-500 text-center">Fournisseur ou charge</span>
                  </button>
                </div>
              )}

              {payStep === 'achat' && (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={goFacture}
                    className="flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcFileText className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900 text-center">Nouvelle<br />facture</span>
                  </button>
                  <button
                    onClick={goCommande}
                    className="flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcClipboardList className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900 text-center">Nouvelle<br />commande</span>
                  </button>
                </div>
              )}

              {/* Cartes Fournisseur / Charge : même hauteur (8.5rem = 136 px mesurés) que les cartes
                  Achat / Paiement du menu — owner 2026-09-10. */}
              {payStep === 'type' && (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => choosePayType('supplier')}
                    className="min-h-[8.5rem] justify-center flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcTruck className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900">Fournisseur</span>
                  </button>
                  <button
                    onClick={() => choosePayType('charge')}
                    className="min-h-[8.5rem] justify-center flex flex-col items-center gap-2 p-6 rounded-2xl border-[1.5px] border-gray-900 bg-gray-100 hover:bg-gray-200 transition"
                  >
                    <IcReceipt className="text-3xl text-gray-900" />
                    <span className="font-semibold text-gray-900">Charge</span>
                  </button>
                </div>
              )}

              {payStep === 'form' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {payType === 'supplier' ? 'Fournisseur' : 'Charge'}
                    </label>
                    {payTargetsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                        <Sk w={40} h={10} className="sk-inline" /> Chargement…
                      </div>
                    ) : (
                      <select
                        value={payTargetId}
                        onChange={(e) => setPayTargetId(e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
                      >
                        <option value="">— Sélectionner {payType === 'supplier' ? 'un fournisseur' : 'une charge'} —</option>
                        {payTargets.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    )}
                    {!payTargetsLoading && payTargets.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">Aucun élément disponible.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant payé (DT)</label>
                    <input
                      type="number" min="0" step="0.001" inputMode="decimal"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      placeholder="0.000"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-lg font-semibold focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                    <input
                      type="text"
                      value={payNote}
                      onChange={(e) => setPayNote(e.target.value)}
                      placeholder="Référence, motif…"
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer (form only) */}
            {payStep === 'form' && (
              <div className="flex gap-3 p-5 border-t border-gray-100 shrink-0">
                <button
                  onClick={() => { setPayStep('type'); }}
                  disabled={paySubmitting}
                  className="btn-g flex-1 py-2.5 disabled:opacity-50"
                >
                  Retour
                </button>
                <button
                  onClick={submitPayment}
                  disabled={paySubmitting}
                  className="btn-p flex-1 py-2.5 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {paySubmitting && <BtnSk />}
                  Enregistrer le paiement
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
